import { get } from 'lodash-es'

import {
  ProjectFileUsage,
  requiredFileEntries,
  requiredFilesFor,
  type ProjectFileUsageType,
} from '@autoriz/shared/files'
import { AccountType } from 'src/utils/enums'
import {
  ProjectFileUsageMeta,
  isFileUsageVisible,
} from 'src/utils/projectFiles'
import { ProjectStep, type ProjectStepType } from '@autoriz/shared/project'

import type { DocumentSlotDefinition } from '../types'

// Friendlier hints for the dossier photos. Each states what the photo is for,
// so the salesman knows why it is asked.
//
// Format rules deliberately do NOT live in these hints any more. Repeating
// "HEIC et PDF ne sont pas acceptés" on three slots told the salesman his
// iPhone photos would be refused three times over and never once told him what
// to do about it — and HEIC is the iPhone factory default, so this is the
// single most common way an on-site upload fails (#584). The accepted formats
// and the two-tap iPhone fix now render once per slot from
// `PHOTO_FORMAT_GUIDANCE`, next to the upload control where they are
// actionable, instead of buried in a descriptive sentence.
const HINT_OVERRIDES: Partial<Record<ProjectFileUsageType, string>> = {
  [ProjectFileUsage.HOUSE_PICTURE]:
    "Vue d'ensemble de la façade concernée par les travaux, pour situer le projet.",
  [ProjectFileUsage.STREET_PICTURE]:
    "Depuis la voie publique, pour montrer l'environnement et l'insertion du projet.",
  [ProjectFileUsage.GARDEN_PICTURE]:
    "Vue d'ensemble du terrain et des abords, pour apprécier l'implantation.",
}

/**
 * Format guidance shown on the photo slots, next to the upload control.
 *
 * The remedy is the part that matters: an iPhone shoots HEIC out of the box, and
 * a salesman standing in a client's garden needs the setting to change, not the
 * name of a codec. Three routes, rendered in this order — see the note on
 * `settings` for why the order is not cosmetic.
 */
export const PHOTO_FORMAT_GUIDANCE = {
  accepted: 'Formats acceptés : JPG ou PNG.',
  // Ordered on purpose: settings change → already-taken photos → web converter.
  // The first two keep the client's photos on the phone. The converter is the
  // fallback, not the headline, because it sends photos of a client's home to a
  // third party — see `converter.caution`.
  settings:
    'Réglages → Appareil photo → Formats → « Plus compatible ». Les prochaines photos seront en JPG.',
  existing:
    'App Photos → Partager → Options → désactivez « Toutes les données des photos », ou envoyez-les-vous par e-mail — elles arrivent en JPG.',
  converter: {
    // Sits under the heading "Sinon, convertissez-les en ligne", so it must not
    // repeat it — it says what the service costs and asks for, nothing else.
    lead: 'Gratuit, sans inscription :',
    label: 'cloudconvert.com/heic-to-jpg',
    url: 'https://cloudconvert.com/heic-to-jpg',
    caution:
      "Ce service est externe : n'y envoyez rien d'autre que des photos du chantier.",
  },
} as const

/** Photo usages that carry `PHOTO_FORMAT_GUIDANCE` under their upload grid. */
export const PHOTO_USAGES: ReadonlyArray<ProjectFileUsageType> = [
  ProjectFileUsage.HOUSE_PICTURE,
  ProjectFileUsage.STREET_PICTURE,
  ProjectFileUsage.GARDEN_PICTURE,
]

/**
 * Structural visibility only — deliberately asked WITHOUT the usage's files.
 *
 * These slots are the project page's checklist of what the client still owes, so
 * an empty slot must stay listed. Omitting the file context makes the rule
 * model's `non-empty` condition count as satisfied (see
 * `DocumentAccessContext`), which is exactly the question a checklist asks.
 */
const isVisibleForSalesman = (
  usage: ProjectFileUsageType,
  step: ProjectStepType
): boolean => {
  if (!get(ProjectFileUsageMeta, usage)) return false
  return isFileUsageVisible(AccountType.SALESMAN, step, usage)
}

// Returns the salesman's mandatory-document slots for a project at its current
// step. The set is the single shared `PROJECT_INITIALIZED_REQUIRED_FILES`
// (photos + drawing views + procuration + existing survey), narrowed by the
// project type (a POOL drops the drawing views, 3D insertion and existing
// survey) and filtered to the usages whose `visible[SALESMAN]` includes the
// step. Titles come from `ProjectFileUsageMeta`; the required count is the
// shared per-usage minimum.
export const getDocumentSlotDefinitions = (project: {
  step: ProjectStepType | string
  projectType?: string | null
}): ReadonlyArray<DocumentSlotDefinition> => {
  const step = project.step as ProjectStepType
  // Salesman intake set for the PROJECT_INITIALIZED gate, resolved from the
  // shared requirement rules (account-type dimension = SALESMAN, project-type
  // dimension = the project's type so pool dossiers shed the views/survey),
  // then filtered to the usages visible to a salesman at the project's step.
  return requiredFileEntries(
    requiredFilesFor({
      step: ProjectStep.PROJECT_INITIALIZED,
      accountType: AccountType.SALESMAN,
      projectType: project.projectType,
    })
  )
    .filter(([usage]) => isVisibleForSalesman(usage, step))
    .map(([usage, required]) => {
      const meta = get(ProjectFileUsageMeta, usage)
      return {
        title: meta?.title ?? usage,
        hint: HINT_OVERRIDES[usage] ?? meta?.description ?? '',
        usage,
        required,
      }
    })
}
